/**
 * PATH: apps/qa/src/pages/record-detail/JobQaRecordWorkspace.tsx
 * FILE_ID: QA-RECORD-JOBHUB-WORKSPACE-001
 * PURPOSE: Mount the shared Job Hub QA workspace on a QA Hub record.
 * WHY: One QA. apps/qa keeps login and the record URL; the body is JobQaTab.
 *      Import JobQaTab through the package subpath, never the @quotech/job-hub
 *      barrel. The barrel also statically exports JobQaTab and pulls invoices,
 *      schedule, and money into the QA Vite graph (build-qa OOM at ~2 GB).
 */
import { useQuery } from '@tanstack/react-query'
import {
  JobHubDataAdapterProvider,
  useJobHubDataAdapter,
} from '@quotech/job-hub/jobs/jobHubDataAdapter'
import type { QuoteRow } from '@quotech/job-hub/jobs/jobDetailTypes'
import { JobQaTab } from '@quotech/job-hub/jobs/tabs/JobQaTab'
import { useQaQuoteBrand } from './useQaQuoteBrand'
import { useQaRecordDataAdapter } from './useQaRecordDataAdapter'

export function JobQaRecordWorkspace({
  jobId,
  focusInspectionId,
}: {
  jobId: number
  focusInspectionId?: string | null
}) {
  useQaQuoteBrand()
  const { dataAdapter, numericOrgId } = useQaRecordDataAdapter()
  return (
    <JobHubDataAdapterProvider adapter={dataAdapter}>
      <JobQaRecordQuotes jobId={jobId} focusInspectionId={focusInspectionId} orgId={numericOrgId} />
    </JobHubDataAdapterProvider>
  )
}

function JobQaRecordQuotes({
  jobId,
  focusInspectionId,
  orgId,
}: {
  jobId: number
  focusInspectionId?: string | null
  orgId?: number
}) {
  const { client } = useJobHubDataAdapter()
  const { data: quotes = [], isLoading } = useQuery<QuoteRow[]>({
    queryKey: ['quotes', { job_id: jobId }],
    queryFn: async () => {
      const response = await client.get('/quotes', { params: { job_id: jobId } })
      const payload = response.data
      if (Array.isArray(payload)) return payload
      return Array.isArray(payload?.results) ? payload.results : []
    },
    enabled: Number.isFinite(jobId),
  })
  return (
    <div data-testid="qa-shared-workspace" className="md:min-h-[28rem] max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-1 max-md:flex-col max-md:overflow-hidden max-md:px-0">
      <JobQaTab
        jobId={jobId}
        orgId={orgId}
        quotes={quotes}
        quotesLoading={isLoading}
        focusInspectionId={focusInspectionId}
        openSetup
      />
    </div>
  )
}
